/**
 * Whether a human has reviewed one generated_contents row — a plain flag,
 * distinct from ReviewStatus (review-status.ts), which is the richer
 * PENDING/IN_REVIEW/RECOMMENDED/CHANGES_REQUESTED/REJECTED verdict shared by
 * pipeline_executions and generation_jobs.
 *
 * Content review turned out not to need that nuance: a reviewer either has
 * looked at this piece of content or hasn't, and anything more specific goes
 * in the free-text remarks field. See migration 060.
 *
 * Advisory only, same as ReviewStatus: approve() never reads this.
 */
export type ContentReviewStatus = "NOT_REVIEWED" | "REVIEWED";

export const ALL_CONTENT_REVIEW_STATUSES: readonly ContentReviewStatus[] = [
  "NOT_REVIEWED",
  "REVIEWED",
];

export function isContentReviewStatus(value: string): value is ContentReviewStatus {
  return (ALL_CONTENT_REVIEW_STATUSES as readonly string[]).includes(value);
}

export const CONTENT_REVIEW_STATUS_META: Record<
  ContentReviewStatus,
  { label: string; hint: string; badge: "pending" | "approved" }
> = {
  NOT_REVIEWED: {
    label: "Not reviewed",
    hint: "Nobody has checked this yet",
    badge: "pending",
  },
  REVIEWED: {
    label: "Reviewed",
    hint: "A reviewer has checked this",
    badge: "approved",
  },
};

/** Toggling is always allowed except to the same value — same reasoning as
 *  canTransitionReview, kept as its own function so callers of the content
 *  axis never need to import the job/pipeline one. */
export function canTransitionContentReview(
  from: ContentReviewStatus,
  to: ContentReviewStatus,
): boolean {
  return from !== to;
}
